import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

import { Grid, Heading, LinkButton, Stack, Text } from '@eluve/components';

export interface EntityNotFoundPageProps {
  entityType: string;
  listPathTemplate?: string;
  listLabel?: string;
}

const interpolate = (
  template: string,
  params: Readonly<Record<string, string | undefined>>,
): string =>
  template.replace(/:([a-zA-Z][a-zA-Z0-9_]*)/g, (match, key: string) => {
    const value = params[key];
    return value ?? match;
  });

export const EntityNotFoundPage: React.FC<EntityNotFoundPageProps> = ({
  entityType,
  listPathTemplate,
  listLabel,
}) => {
  const params = useParams();
  const listPath = listPathTemplate
    ? interpolate(listPathTemplate, params)
    : undefined;

  return (
    <>
      <Helmet>
        <title>{entityType} not found | Eluve</title>
      </Helmet>
      <Grid place="center" className="row-span-full px-6 text-center lg:px-8">
        <Stack direction="vertical" align="center" gap={6}>
          <Text size="md" weight="semibold">
            404
          </Text>
          <Heading size="3xl" weight="bold" level={1}>
            {entityType} not found
          </Heading>
          <Text size="md" color="secondary">
            Sorry, we couldn’t find the {entityType.toLowerCase()} you’re
            looking for. It may have been deleted or the link may be incorrect.
          </Text>
          <Stack direction="horizontal" align="center" justify="center" gap={3}>
            {listPath && (
              <LinkButton to={listPath}>
                {listLabel ?? `Back to ${entityType.toLowerCase()}s`}
              </LinkButton>
            )}
            <LinkButton to="/" variant="outline">
              Go home
            </LinkButton>
          </Stack>
        </Stack>
      </Grid>
    </>
  );
};
